import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';

export enum NotificationType {
  SUCCESS = 'success',
  ERROR = 'error',
  INFO = 'info',
  WARNING = 'warning'
}

/**
 * Notification category distinguishes different notification types
 * Each category can have its own display format in the template
 */
export type NotificationCategory = 'autofill' | 'validation' | 'save' | 'generic';

/**
 * Structured detail item for notification
 */
export interface NotificationDetail {
  label: string;
  value: string | number;          // Truncated display value
  originalValue?: string | number; // Full untruncated value for tooltip
}

export interface Notification {
  id: string;
  type: NotificationType;
  message: string;
  details?: NotificationDetail[];
  category?: NotificationCategory;
  // Auto-fill specific fields (deprecated - use details)
  documentName?: string;
  fileName?: string;
  // Validation specific fields (deprecated - use details)
  documentModel?: string;
  timestamp: number;
}

@Injectable({
  providedIn: 'root'
})
export class NotificationService {
  private notificationQueue$ = new BehaviorSubject<Notification[]>([]);
  private idCounter = 0;
  private currentDismissTimer?: any;
  private currentTimerNotificationId?: string | null = null;
  private isPaused = false;
  private timerStartedAt = 0;   // When the current timer was started (ms epoch)
  private pausedAt = 0;         // When the timer was paused (ms epoch)
  private readonly AUTO_DISMISS_MS = 5000;
  private readonly MAX_NAME_LENGTH = 35; // Maximum display length for names

  constructor() {
    // Defensive: ensure a timer is scheduled whenever there's a first notification
    this.notificationQueue$.subscribe(queue => {
      const first = queue[0];
      if (!first) {
        // if queue empty, clear any timer
        if (this.currentDismissTimer) {
          clearTimeout(this.currentDismissTimer);
          this.currentDismissTimer = undefined;
          this.currentTimerNotificationId = null;
        }
        return;
      }

      // If no timer scheduled or it's for a different notification, schedule one
      if (!this.currentDismissTimer || this.currentTimerNotificationId !== first.id) {
        this.scheduleAutoDismiss(first.id);
      }
    });
  }

  /**
   * Get current notification queue
   */
  getNotifications(): Observable<Notification[]> {
    return this.notificationQueue$.asObservable();
  }

  /**
   * Truncate long names intelligently using First2Words...LastWord pattern
   * Examples:
   * - "1-4_family_rider_long_document_name" → "1-4 family...name"
   * - "very_long_filename_with_many_words.pdf" → "very long...words.pdf"
   * - "uniform_residential_loan_application" → "uniform residential...application"
   * @param name The name to truncate
   * @param maxLength Maximum length (default: 35)
   * @returns Truncated name or original if shorter than maxLength
   */
  private truncateName(name: string, maxLength: number = this.MAX_NAME_LENGTH): string {
    if (!name || name.length <= maxLength) {
      return name;
    }

    // Separate file extension if present
    const lastDotIndex = name.lastIndexOf('.');
    const hasExtension = lastDotIndex > 0 && lastDotIndex > name.length - 6; // Extension up to 5 chars
    const extension = hasExtension ? name.substring(lastDotIndex) : '';
    const nameWithoutExt = hasExtension ? name.substring(0, lastDotIndex) : name;

    // Split by common delimiters (underscore, dash, space) to get words
    const words = nameWithoutExt.split(/[_\-\s]+/).filter(w => w.length > 0);
    
    if (words.length === 0) {
      // No words found, just truncate from start
      return name.substring(0, maxLength - 3) + '...';
    }

    if (words.length === 1) {
      // Single word, truncate it
      const availableLength = maxLength - extension.length - 3; // Reserve for "..." and extension
      return words[0].substring(0, Math.max(availableLength, 5)) + '...' + extension;
    }

    if (words.length === 2) {
      // Two words: show both if they fit, otherwise truncate
      const combined = words.join(' ') + extension;
      if (combined.length <= maxLength) {
        return combined;
      }
      // Truncate: FirstWord...SecondWord
      const ellipsis = '...';
      const truncated = words[0] + ellipsis + words[1] + extension;
      if (truncated.length <= maxLength) {
        return truncated;
      }
      // Still too long, shorten words
      const availableLength = maxLength - ellipsis.length - extension.length;
      const firstPartLength = Math.floor(availableLength / 2);
      const lastPartLength = availableLength - firstPartLength;
      return words[0].substring(0, Math.max(firstPartLength, 3)) + 
             ellipsis + 
             words[1].substring(Math.max(0, words[1].length - lastPartLength), words[1].length) + 
             extension;
    }

    // 3+ words: use FirstWord SecondWord...LastWord pattern
    const firstWord = words[0];
    const secondWord = words[1];
    const lastWord = words[words.length - 1];
    const ellipsis = '...';
    const combined = firstWord + ' ' + secondWord + ellipsis + lastWord + extension;

    if (combined.length <= maxLength) {
      return combined;
    }

    // Still too long, truncate the words themselves
    const availableLength = maxLength - ellipsis.length - extension.length - 1; // -1 for space
    const firstPartLength = Math.floor(availableLength / 3);
    const secondPartLength = Math.floor(availableLength / 3);
    const lastPartLength = availableLength - firstPartLength - secondPartLength;
    
    return firstWord.substring(0, Math.max(firstPartLength, 2)) + ' ' +
           secondWord.substring(0, Math.max(secondPartLength, 2)) + 
           ellipsis + 
           lastWord.substring(Math.max(0, lastWord.length - lastPartLength), lastWord.length) + 
           extension;
  }

  /**
   * Show success notification for auto-fill completion
   * @param documentName The document model name
   * @param fileName The file name
   * @param fieldsCount Number of fields auto-filled
   * @param overrideCount Optional count of overridden fields
   */
  showAutoFillSuccess(documentName: string, fileName: string, fieldsCount: number, overrideCount?: number): void {
    const message = 'Autofill completed successfully.';
    
    const details: NotificationDetail[] = [
      { label: 'Document model', value: this.truncateName(documentName), originalValue: documentName },
      { label: 'File', value: this.truncateName(fileName), originalValue: fileName }
    ];
    
    if (overrideCount !== undefined && overrideCount > 0) {
      details.push({ label: 'Replaced', value: `${overrideCount} fields` });
    }
    
    this.addNotification({
      type: NotificationType.SUCCESS,
      category: 'autofill',
      message,
      details,
      documentName: this.truncateName(documentName),
      fileName: this.truncateName(fileName)
    });
  }

  /**
   * Show error notification for auto-fill failure
   * @param documentName The document model name
   * @param fileName The file name
   * @param message The main error message (e.g., "Autofill Failed.")
   * @param detailLabel Optional label for additional detail (e.g., "Reason", "Error")
   * @param detailValue Optional value for additional detail
   */
  showAutoFillError(documentName: string, fileName: string, message: string, detailLabel?: string, detailValue?: string): void {
    const details: NotificationDetail[] = [
      { label: 'Document model', value: this.truncateName(documentName), originalValue: documentName },
      { label: 'File', value: this.truncateName(fileName), originalValue: fileName }
    ];
    
    if (detailLabel && detailValue) {
      details.push({ label: detailLabel, value: detailValue, originalValue: detailValue });
    }
    
    this.addNotification({
      type: NotificationType.ERROR,
      category: 'autofill',
      message,
      details,
      documentName: this.truncateName(documentName),
      fileName: this.truncateName(fileName)
    });
  }

  /**
   * Show generic success notification
   * @param message The success message
   * @param details Optional structured details
   */
  showSuccess(message: string, details?: NotificationDetail[]): void {
    // Truncate display value but preserve original for tooltip
    const truncatedDetails = details?.map(d => ({
      ...d,
      originalValue: d.originalValue ?? d.value, // keep full original before truncation
      value: typeof d.value === 'string' ? this.truncateName(d.value) : d.value
    }));
    
    this.addNotification({
      type: NotificationType.SUCCESS,
      category: 'generic',
      message,
      details: truncatedDetails
    });
  }

  /**
   * Show generic error notification
   * @param message The error message
   * @param details Optional structured details
   */
  showError(message: string, details?: NotificationDetail[]): void {
    // Truncate display value but preserve original for tooltip
    const truncatedDetails = details?.map(d => ({
      ...d,
      originalValue: d.originalValue ?? d.value, // keep full original before truncation
      value: typeof d.value === 'string' ? this.truncateName(d.value) : d.value
    }));
    
    this.addNotification({
      type: NotificationType.ERROR,
      category: 'generic',
      message,
      details: truncatedDetails
    });
  }

  /**
   * Show success toaster for validation (evaluation/test) completion
   * Persists across page navigation - visible at top of any page
   * @param documentModel The document model name
   * @param runType The run type - 'part' for test run, 'all' for evaluation run
   * @param failedFiles Number of failed files (optional)
   */
  showValidationSuccess(documentModel: string, runType: 'all' | 'part', failedFiles: number = 0): void {
    const runTypeLabel = runType === 'part' ? 'Test' : 'Evaluation';
    const message = `${runTypeLabel} run completed successfully`;
    
    // Build structured details
    const details: NotificationDetail[] = [
      { label: 'Document model', value: this.truncateName(documentModel), originalValue: documentModel }
    ];
    
    if (failedFiles > 0) {
      details.push({ label: 'Failed files', value: failedFiles });
    }
    
    this.addNotification({
      type: NotificationType.SUCCESS,
      category: 'validation',
      message,
      details,
      documentModel: this.truncateName(documentModel)
    });
  }

  /**
   * Show failure toaster for validation (evaluation/test) failure
   * Persists across page navigation - visible at top of any page
   * @param documentModel The document model name
   * @param runType The run type - 'part' for test run, 'all' for evaluation run
   * @param errorReason Optional error reason to display
   */
  showValidationFailed(documentModel: string, runType: 'all' | 'part', errorReason?: string): void {
    const runTypeLabel = runType === 'part' ? 'Test' : 'Evaluation';
    const details: NotificationDetail[] = [
      { label: 'Document model', value: this.truncateName(documentModel), originalValue: documentModel }
    ];
    
    if (errorReason) {
      details.push({ label: 'Reason', value: errorReason, originalValue: errorReason });
    }
    
    this.addNotification({
      type: NotificationType.ERROR,
      category: 'validation',
      message: `${runTypeLabel} run not initiated.`,
      details,
      documentModel: this.truncateName(documentModel)
    });
  }

  /**
   * Show success toaster for ground truth save
   */
  showSaveSuccess(documentModel?: string, fileName?: string): void {
    const details: NotificationDetail[] = [];
    if (documentModel) {
      details.push({
        label: 'Document model',
        value: this.truncateName(documentModel),
        originalValue: documentModel
      });
    }

    if (fileName) {
      details.push({
        label: 'File',
        value: this.truncateName(fileName),
        originalValue: fileName
      });
    }

    this.addNotification({
      type: NotificationType.SUCCESS,
      category: 'save',
      message: 'Ground truth saved successfully!',
      details: details.length > 0 ? details : undefined,
      documentModel: documentModel ? this.truncateName(documentModel) : undefined,
      fileName: fileName ? this.truncateName(fileName) : undefined
    });
  }

  /**
   * Show notification when user discards unsaved ground truth while leaving
   */
  showSaveDiscarded(documentModel?: string, fileName?: string): void {
    const details: NotificationDetail[] = [];
    if (documentModel) {
      details.push({
        label: 'Document model',
        value: this.truncateName(documentModel),
        originalValue: documentModel
      });
    }

    if (fileName) {
      details.push({
        label: 'File',
        value: this.truncateName(fileName),
        originalValue: fileName
      });
    }

    this.addNotification({
      type: NotificationType.ERROR,
      category: 'save',
      message: 'Ground truth not saved.',
      details: details.length > 0 ? details : undefined
    });
  }

  /**
   * Show error toaster for ground truth save failure
   * @param errorMessage Optional error details (used as detail value with "Reason" label)
   */
  showSaveError(errorMessage?: string): void {
    const details: NotificationDetail[] = [];
    if (errorMessage) {
      details.push({ label: 'Reason', value: errorMessage, originalValue: errorMessage });
    }
    
    this.addNotification({
      type: NotificationType.ERROR,
      category: 'save',
      message: 'Failed to save.',
      details: details.length > 0 ? details : undefined
    });
  }

  /**
   * Show success toaster for run (evaluation/test) initiation
   * Shows document model name - used when all files are running (no skipped files)
   * @param documentModel The document model name
   * @param runType The run type - 'part' for test run, 'all' for evaluation run
   */
  showRunInitiated(documentModel: string, runType: 'all' | 'part' = 'all'): void {
    const runTypeLabel = runType === 'part' ? 'Test' : 'Evaluation';
      
    this.addNotification({
      type: NotificationType.SUCCESS,
      category: 'validation',
      message: `${runTypeLabel} run started successfully`,
      details: [{ label: 'Document model', value: this.truncateName(documentModel), originalValue: documentModel }],
      documentModel: this.truncateName(documentModel)
    });
  }

  /**
   * Show success toaster for file upload completion
   * @param fileName The uploaded file name
   */
  showUploadSuccess(fileName: string): void {
    this.addNotification({
      type: NotificationType.SUCCESS,
      category: 'generic',
      message: 'File uploaded successfully',
      details: [{ label: 'File', value: this.truncateName(fileName), originalValue: fileName }],
      fileName: this.truncateName(fileName)
    });
  }

  /**
   * Show info toaster for autofill/extraction initiation
   * @param documentName The document model name
   * @param fileName The file being processed
   */
  showExtractionInitiated(documentName: string, fileName: string): void {
    this.addNotification({
      type: NotificationType.INFO,
      category: 'autofill',
      message: 'Autofill initiated successfully',
      details: [
        { label: 'Document model', value: this.truncateName(documentName), originalValue: documentName },
        { label: 'File', value: this.truncateName(fileName), originalValue: fileName }
      ],
      documentName: this.truncateName(documentName),
      fileName: this.truncateName(fileName)
    });
  }

  /**
   * Show success toaster for data extraction completion
   * @param documentName The document model name
   * @param fileName The file that was processed
   */
  showExtractionCompleted(documentName: string, fileName: string): void {
    const details: NotificationDetail[] = [
      { label: 'Document model', value: this.truncateName(documentName), originalValue: documentName },
      { label: 'File', value: this.truncateName(fileName), originalValue: fileName }
    ];
    
    this.addNotification({
      type: NotificationType.SUCCESS,
      category: 'autofill',
      message: 'Data extraction completed successfully',
      details,
      documentName: this.truncateName(documentName),
      fileName: this.truncateName(fileName)
    });
  }

  /**
   * Show success toaster for file deletion
   * @param fileName The deleted file name
   */
  showFileDeleted(fileName: string): void {
    this.addNotification({
      type: NotificationType.SUCCESS,
      category: 'generic',
      message: 'File deleted successfully',
      details: [{ label: 'File', value: this.truncateName(fileName), originalValue: fileName }]
    });
  }

  /**
   * Show success toaster for prompt save
   */
  showPromptSaved(documentModel?: string): void {
    this.addNotification({
      type: NotificationType.SUCCESS,
      category: 'generic',
      message: 'Prompt saved successfully!'
    });
  }

  /**
   * Show error toaster for file already exists (duplicate file)
   * @param fileName The conflicting file name
   */
  showFileAlreadyExists(fileName: string): void {
    this.addNotification({
      type: NotificationType.ERROR,
      category: 'generic',
      message: 'File upload failed.',
      details: [
        { label: 'File', value: this.truncateName(fileName), originalValue: fileName },
        { label: 'Reason', value: 'Duplicate File' }
      ]
    });
  }

  /**
   * Show error toaster for file upload failure
   * @param fileName The file name that failed to upload
   * @param errorReason The reason for failure
   */
  showFileUploadError(fileName: string, errorReason: string): void {
    this.addNotification({
      type: NotificationType.ERROR,
      category: 'generic',
      message: 'File upload failed.',
      details: [
        { label: 'File', value: this.truncateName(fileName), originalValue: fileName },
        { label: 'Reason', value: errorReason, originalValue: errorReason }
      ]
    });
  }

  /**
   * Show error toaster for file deletion failure
   * @param fileName The file name that failed to delete
   * @param errorReason The reason for failure
   */
  showFileDeleteError(fileName: string, errorReason: string): void {
    this.addNotification({
      type: NotificationType.ERROR,
      category: 'generic',
      message: 'File deletion failed.',
      details: [
        { label: 'File', value: this.truncateName(fileName), originalValue: fileName },
        { label: 'Reason', value: errorReason, originalValue: errorReason }
      ]
    });
  }

  /**
   * Show error toaster for attribute schema load failure
   * @param errorReason The reason for failure
   */
  showAttributeSchemaError(errorReason: string): void {
    this.addNotification({
      type: NotificationType.ERROR,
      category: 'generic',
      message: 'Attribute schema not loaded.',
      details: [
        { label: 'Reason', value: errorReason, originalValue: errorReason }
      ]
    });
  }

  /**
   * Pause auto-dismiss timer (called on mouse hover).
   * Records exactly how much time was remaining so resume can continue from there.
   */
  pauseDismiss(): void {
    if (this.currentDismissTimer) {
      clearTimeout(this.currentDismissTimer);
      this.currentDismissTimer = undefined;
      this.pausedAt = Date.now();
    }
    this.isPaused = true;
  }

  /**
   * Resume auto-dismiss timer from where it was paused (not a fresh 5s).
   * Clamps remaining time to minimum 500ms so it never vanishes instantly.
   */
  resumeDismiss(): void {
    this.isPaused = false;
    if (this.currentTimerNotificationId) {
      const elapsed = this.pausedAt - this.timerStartedAt;
      const remaining = Math.max(500, this.AUTO_DISMISS_MS - elapsed);
      this.scheduleAutoDismiss(this.currentTimerNotificationId, remaining);
    }
  }

  /**
   * Add notification to queue
   */
  private addNotification(notification: Omit<Notification, 'id' | 'timestamp'>): void {
    const newNotification: Notification = {
      ...notification,
      id: `notif-${++this.idCounter}-${Date.now()}`,
      timestamp: Date.now()
    };

    const currentQueue = this.notificationQueue$.value;
    const updatedQueue = [...currentQueue, newNotification];
    this.notificationQueue$.next(updatedQueue);
    // If this is the only notification, schedule its auto-dismiss
    if (updatedQueue.length === 1) {
      this.scheduleAutoDismiss(newNotification.id);
    }
  }

  /**
   * Dismiss notification by ID
   */
  dismiss(id: string): void {
    // Clear any running dismiss timer for this notification
    if (this.currentTimerNotificationId === id && this.currentDismissTimer) {
      clearTimeout(this.currentDismissTimer);
      this.currentDismissTimer = undefined;
      this.currentTimerNotificationId = null;
    }

    const currentQueue = this.notificationQueue$.value;
    const updatedQueue = currentQueue.filter(n => n.id !== id);
    this.notificationQueue$.next(updatedQueue);

    // If there is another notification now first in queue, schedule its auto-dismiss
    if (updatedQueue.length > 0) {
      this.scheduleAutoDismiss(updatedQueue[0].id);
    }
  }

  /**
   * Schedule auto-dismiss for the notification with given id
   */
  private scheduleAutoDismiss(notificationId: string, duration: number = this.AUTO_DISMISS_MS): void {
    // Clear existing timer first
    if (this.currentDismissTimer) {
      clearTimeout(this.currentDismissTimer);
      this.currentDismissTimer = undefined;
      this.currentTimerNotificationId = null;
    }
    this.currentTimerNotificationId = notificationId;
    this.timerStartedAt = Date.now(); // Record when this timer started
    this.currentDismissTimer = setTimeout(() => {
      // Defensive: dismiss only if the notification still exists
      const currentQueue = this.notificationQueue$.value;
      const exists = currentQueue.some(n => n.id === notificationId);
      if (exists) {
        this.dismiss(notificationId);
      } else {
        // If not present, schedule next one if queue non-empty
        if (currentQueue.length > 0) {
          this.scheduleAutoDismiss(currentQueue[0].id);
        }
      }
    }, duration);
  }

  /**
   * Remove any notifications that contain the given substring in their message
   */
  clearNotificationsContaining(substring: string): void {
    const currentQueue = this.notificationQueue$.value;
    const updatedQueue = currentQueue.filter(n => !n.message.includes(substring));
    if (updatedQueue.length !== currentQueue.length) {
      this.notificationQueue$.next(updatedQueue);
      // Reset timers and schedule next
      if (this.currentDismissTimer) {
        clearTimeout(this.currentDismissTimer);
        this.currentDismissTimer = undefined;
        this.currentTimerNotificationId = null;
      }
      if (updatedQueue.length > 0) {
        this.scheduleAutoDismiss(updatedQueue[0].id);
      }
    }
  }

  /**
   * Clear all notifications
   */
  clearAll(): void {
    this.notificationQueue$.next([]);
  }
}
